import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { isValidCoord } from "src/utils/helpers";

interface Props {
    map: google.maps.Map | null;
    pickUpCoords?: google.maps.LatLngLiteral;
    dropOffCoords?: google.maps.LatLngLiteral;
}

const defaultMeta = { distance: "", duration: "" };

const useDirections = ({ map, pickUpCoords, dropOffCoords }: Props) => {
    const service = useRef<google.maps.DirectionsService | null>(null);
    const renderer = useRef<google.maps.DirectionsRenderer | null>(null);
    const checkedOrigin = useRef<google.maps.LatLngLiteral>();
    const checkedDest = useRef<google.maps.LatLngLiteral>();

    const [routeMeta, setRouteMeta] = useState(defaultMeta);
    const [directions, setDirections] =
        useState<google.maps.DirectionsResult | null>(null);

    const reset = useCallback(() => {
        setDirections(null);
        renderer.current?.setMap(null);
        setRouteMeta(defaultMeta);
    }, []);

    const options = useMemo(() => {
        return {
            origin: pickUpCoords || "",
            destination: dropOffCoords || "",
            provideRouteAlternatives: false,
            travelMode: window?.google?.maps?.TravelMode
                ? google.maps.TravelMode.DRIVING
                : ("DRIVING" as google.maps.TravelMode),
        };
    }, [pickUpCoords, dropOffCoords]);

    const callback = useCallback(
        (
            result: google.maps.DirectionsResult | null,
            status: google.maps.DirectionsStatus
        ) => {
            if (status !== "OK" || !result) {
                reset();
                return;
            }
            renderer.current?.setMap(map);
            renderer.current?.setDirections(result);
            setDirections(result);
            setRouteMeta({
                distance: result?.routes[0]?.legs[0]?.distance?.text ?? "",
                duration: result?.routes[0]?.legs[0]?.duration?.text ?? "",
            });
        },
        [map, renderer, reset]
    );

    useEffect(() => {
        try {
            // only procceed if map is loading
            if (!map) return;

            // check and initialize service and renderer
            if (!service.current) {
                service.current = new google.maps.DirectionsService();
            }

            if (!renderer.current) {
                renderer.current = new google.maps.DirectionsRenderer();
            }

            // plot directions when both coordinates are valid. Recompute if either
            // coordinate changed since the previous request
            if (
                isValidCoord(pickUpCoords) &&
                isValidCoord(dropOffCoords)
            ) {
                if (
                    pickUpCoords !== checkedOrigin.current ||
                    dropOffCoords !== checkedDest.current
                ) {
                    service.current.route(options, callback);
                    checkedOrigin.current = pickUpCoords;
                    checkedDest.current = dropOffCoords;
                }
            } else {
                reset();
                checkedOrigin.current = undefined;
                checkedDest.current = undefined;
            }
        } catch (error) {
            console.error(error);
        }
    }, [
        map,
        reset,
        service,
        renderer,
        options,
        callback,
        pickUpCoords,
        dropOffCoords,
    ]);

    return { routeMeta, directions };
};

export default useDirections;
